import React, {useState,useEffect,useRef} from 'react';
import {MailIcon,RefreshIcon} from '@heroicons/react/outline';
import {enqueueSnackbar} from 'notistack';
import ReCAPTCHA from 'react-google-recaptcha';
import submitFormUtil from '../../utils/submit-form-util';
import locales from '../../locales/locales';
import config from '../../constants/config';
const EmailVerifier=({defaultEmail,shallCheckForEmailExistence,onExit,globalSettings})=>{
  const [stage,setStage]=useState(1);
  const [email,setEmail]=useState(defaultEmail||'');
  const [recaptchaValue,setRecaptchaValue]=useState(null);
  const [code,setCode]=useState(Array(6).fill(' ').join(''));
  const [emailError,setEmailError]=useState(null);
  const [codeError,setCodeError]=useState(null);
  const [isLoading,setIsLoading]=useState(false);
  const codeInputRefs=[useRef(null),useRef(null),useRef(null),useRef(null),useRef(null),useRef(null)];
  useEffect(()=>{
    setRecaptchaValue(null);
    setCode(Array(6).fill(' ').join(''));
  },[stage]);
  useEffect(()=>{
    if(emailError){
      setEmailError(null);
    }
  },[email]);
  useEffect(()=>{
    if(codeError){
      setCodeError(null);
    }
  },[code]);
  const handleNext=async(e)=>{
    e.preventDefault();
    if(isLoading){
      return;
    }
    if(!recaptchaValue){
      setEmailError(locales.emailVerifierRecaptchaError);
      return;
    }
    setIsLoading(true);
    let ok=true;
    if(shallCheckForEmailExistence){
      ok=false;
      const res=await submitFormUtil('/login',{
        'email':[email,setEmailError],
        'password':[Array(8).fill('a').join(''),()=>{}],
      },{
        'no_such_email':{
          'email':locales.emailVerifierResetPasswordOnly,
        },
        'password_incorrect':(()=>{
          ok=true;
        }),
      });
    }
    if(ok){
      const res=await submitFormUtil('/check-email',{
        'email':[email,setEmailError],
        'captcha':[recaptchaValue,()=>{}],
      },{
        'captcha_invalid':null,
      });
      if(res){
        setEmailError(null);
        setStage(2);
      }
    }
    setIsLoading(false);
  };
  const handleCodeChange=(index,value)=>{
    if(value&&!('0'.charCodeAt(0)<=value.charCodeAt(0)&&value.charCodeAt(0)<='9'.charCodeAt(0))){
      return;
    }
    setCode(code.substr(0,index)+(value[0]||' ')+code.substr(index+1));
    if(value&&index<5){
      codeInputRefs[index+1].current.focus();
    }
  };
  const handleKeyDown=(e,index)=>{
    if(e.key==='Backspace'&&code[index]===' '&&index>0){
      codeInputRefs[index-1].current.focus();
    }
  };
  const handlePaste=(e,index)=>{
    e.preventDefault();
    const digits=e.clipboardData.getData('text').replace(/\D/g,'').substr(0,6-index);
    setCode(code.substr(0,index)+digits+code.substr(index+digits.length));
    codeInputRefs[Math.min(5,index+digits.length)].current.focus();
  };
  const handleConfirm=async(e)=>{
    e.preventDefault();
    if(isLoading){
      return;
    }
    setIsLoading(true);
    const res=await submitFormUtil('/check-code',{
      'code':[code,setCodeError],
      'email':[email,()=>{}],
    },{
      'code_invalid':(()=>{
        enqueueSnackbar(
          locales.emailVerifierCodeInvalid[globalSettings.lang]
        ,{variant:'error'});
        setStage(1);
      })
    });
    setIsLoading(false);
    if(res){
      enqueueSnackbar(
        locales.emailVerifierSuccess[globalSettings.lang]
      ,{variant:'success'});
      onExit([code,email]);
    }
  };
  return (
    <div className='fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50'>
      <form
        onSubmit={stage===1?handleNext:handleConfirm}
        className='bg-white rounded-xl shadow-lg max-w-md w-full mx-4 p-6 relative'
      >
        <h2 className='text-2xl font-bold text-center text-gray-900 mb-6'>
          {
            stage===1?
            locales.emailVerifierHeadingStage1[globalSettings.lang]:
            locales.emailVerifierHeadingStage2[globalSettings.lang]
          }
        </h2>
        {stage===1?(
          <>
            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                {locales.emailVerifierEmailLabel[globalSettings.lang]}
              </label>
              <div className='relative'>
                <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                  <MailIcon className='h-5 w-5 text-gray-400' />
                </div>
                <input
                  type='email'
                  value={email}
                  onChange={(e)=>{setEmail(e.target.value);}}
                  disabled={defaultEmail!==undefined}
                  placeholder='your.email@example.com'
                  className={`w-full pl-10 pr-3 py-2 border ${emailError ? 'border-red-300' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600 ${(defaultEmail===undefined)?'':'bg-gray-100'}`}
                />
              </div>
              {emailError&&<p className='mt-1 text-sm text-red-600'>{emailError[globalSettings.lang]}</p>}
            </div>
            <div className='flex justify-center mb-6'>
              <ReCAPTCHA
                sitekey={config.RECAPTCHA_SITEKEY}
                onChange={setRecaptchaValue}
              />
            </div>
          </>
        ):(
          <>
            <div className='mb-6'>
              <p className='text-gray-600 mb-2'>
                {locales.emailVerifierCodeSent[globalSettings.lang]}
                <span className='font-medium'>{email}</span>
              </p>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                {locales.emailVerifierCodeLabel[globalSettings.lang]}
              </label>
              <div className='flex justify-between gap-2 mb-1'>
                {Array(6).fill().map((_,i) => (
                  <input
                    key={i}
                    ref={codeInputRefs[i]}
                    type='text'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    maxLength='1'
                    value={code[i]===' '?'':code[i]}
                    onChange={(e)=>{handleCodeChange(i,e.target.value);}}
                    onKeyDown={(e)=>{handleKeyDown(e,i);}}
                    onPaste={(e)=>{handlePaste(e,i);}}
                    className={`w-full aspect-square text-center text-xl font-medium border ${codeError ? 'border-red-300' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600`}
                  />
                ))}
              </div>
              {codeError&&<p className='mt-1 text-sm text-red-600'>{codeError[globalSettings.lang]}</p>}
            </div>
            <button
              type='button'
              onClick={()=>{setEmailError(locales.emailVerifierDidntReceiveEmail);setStage(1);}}
              className='inline-flex items-center text-purple-700 hover:text-purple-800 mb-6'
            >
              <RefreshIcon className='h-4 w-4 mr-1' />
              {locales.emailVerifierRequestNewCode[globalSettings.lang]}
            </button>
          </>
        )}
        <div className='flex space-x-4'>
          <button
            type='button'
            onClick={()=>{onExit(null);}}
            className='flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-lg font-medium hover:bg-gray-50 transition'
          >
            {locales.emailVerifierCancel[globalSettings.lang]}
          </button>
          <button
            type='submit'
            disabled={isLoading}
            className={`flex-1 bg-purple-800 hover:bg-purple-600 text-white px-4 py-2 rounded-lg text-lg font-medium transition ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {
              isLoading?
              locales.emailVerifierProcessing[globalSettings.lang]:
              (
                stage===1?
                locales.emailVerifierNext[globalSettings.lang]:
                locales.emailVerifierConfirm[globalSettings.lang]
              )
            }
          </button>
        </div>
      </form>
    </div>
  );
};
export default EmailVerifier;

